import { For, Show } from 'solid-js';
import Text from './Text';
import { useTranslation } from '../hooks/useTranslation';
import { toDateTimeInput, fromDateTimeInput } from '../services/dateFormat';
import styles from './HologramWindowPicker.module.css';

export const WINDOWS = ['AllTime', 'Days30', 'Days7', 'Hours24', 'Today', 'Custom'] as const;

const INHERIT = '';

// Matches HologramBindings.MaxWindowLabelText; the backend truncates past it, and {window.label} is
// measured against it.
const MAX_LABEL = 16;

// Optional throughout because a line carries the same four fields as the generated request DTO, where
// every one of them may be absent.
export interface HologramWindowFields {
    window?: string | null;
    windowFrom?: string | null;
    windowTo?: string | null;
    windowLabelText?: string | null;
}

export default function HologramWindowPicker(props: {
    value: HologramWindowFields;
    allowInherit?: boolean;
    dense?: boolean;
    onChange: (patch: Partial<HologramWindowFields>) => void;
}) {
    const { t } = useTranslation();
    const gap = () => (props.dense ? '2px' : '4px');
    const isCustom = () => props.value.window === 'Custom';

    return (
        <div class="v" style={{ gap: gap() }}>
            <label class="v" style={{ gap: gap() }}>
                <Text val="HOLO_FIELD_WINDOW" />
                <select value={props.value.window ?? INHERIT}
                    onChange={(e) => props.onChange({ window: e.currentTarget.value || null })}>
                    <Show when={props.allowInherit}>
                        <option value={INHERIT}>{t('HOLO_LINE_INHERIT')}</option>
                    </Show>
                    <For each={WINDOWS}>
                        {(w) => <option value={w}>{t('HOLO_WINDOW_' + w.toUpperCase())}</option>}
                    </For>
                </select>
            </label>

            <Show when={isCustom()}>
                <div class={styles.range}>
                    <label class="v" style={{ gap: gap() }}>
                        <span class={styles.caption}><Text val="HOLO_WINDOW_FROM" /></span>
                        <input type="datetime-local" value={toDateTimeInput(props.value.windowFrom)}
                            onInput={(e) => props.onChange({
                                windowFrom: fromDateTimeInput(e.currentTarget.value)?.toISOString() ?? null,
                            })} />
                    </label>

                    <label class="v" style={{ gap: gap() }}>
                        <span class={styles.caption}><Text val="HOLO_WINDOW_TO" /></span>
                        <input type="datetime-local" value={toDateTimeInput(props.value.windowTo)}
                            onInput={(e) => props.onChange({
                                windowTo: fromDateTimeInput(e.currentTarget.value)?.toISOString() ?? null,
                            })} />
                    </label>

                    <label class={`v ${styles.labelField}`} style={{ gap: gap() }}>
                        <span class={styles.caption}><Text val="HOLO_WINDOW_LABEL_TEXT" /></span>
                        <input type="text" maxLength={MAX_LABEL}
                            placeholder={t('HOLO_WINDOW_LABEL_TEXT_PLACEHOLDER')}
                            value={props.value.windowLabelText ?? ''}
                            onInput={(e) => props.onChange({ windowLabelText: e.currentTarget.value || null })} />
                    </label>
                </div>

                <span class={styles.hint}><Text val="HOLO_WINDOW_OPEN_ENDED_HINT" /></span>
            </Show>
        </div>
    );
}
